<template>
  <MenuItem v-slot="{ active, close }" :disabled="props.disabled">
    <component
      :is="componentType"
      :href="props.href"
      @click="click(close)"
      :target="target"
      :rel="rel"
      :class="[
        active
          ? 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white'
          : 'text-gray-700 dark:text-gray-200',
        props.disabled ? 'cursor-not-allowed opacity-50' : '',
        'group flex items-center px-4 py-2 text-sm',
        classesForButton,
      ]"
      :data-test="props.dataTest"
      :as="props.linkAs"
      :method="props.method"
    >
      <HeroiconContainer :icon="computedIcon" class="mr-3" />
      <slot>
        {{ computedLabel }}
      </slot>
    </component>
  </MenuItem>
</template>

<script setup>
import { MenuItem } from '@headlessui/vue'
import {
  ArrowDownOnSquareIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  FolderOpenIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  QrCodeIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/vue/20/solid'
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import HeroiconContainer from '../HeroiconContainer.vue'

const props = defineProps({
  href: {
    type: String,
    default: null,
  },
  type: String,
  external: Boolean,
  noBlank: Boolean,
  label: String,
  icon: Function,
  disabled: Boolean,

  linkAs: String, // For AppLayout dropdown logout button
  method: String, // For AppLayout dropdown logout button
  dataTest: String,
})

const emit = defineEmits(['click'])

function click(close) {
  // MenuItem's disabled only styles and skips keyboard activation; direct
  // clicks still land here, so guard them (and keep the menu open).
  if (props.disabled) return

  // Emit click before close, because close will unmount the component and the click will never fire
  emit('click')

  // HEADLESSUI: Call close() no matter what, because even though when this component is a <button> or <a> it will close automatically, when the component is <Link>, the preventDefault() in the <Link> will stop the menu from automatically closing.
  close()
}

const target = computed(() =>
  props.external && !props.noBlank ? '_blank' : null
)

const rel = computed(() => (props.external ? 'noopener noreferrer' : null))

const componentType = computed(() => {
  if (props.href === null) return 'button'
  else if (props.external) return 'a'
  else return Link
})

const classesForButton = computed(() =>
  props.linkAs === 'button' || componentType.value === 'button'
    ? 'w-full text-left'
    : ''
)

const computedLabel = computed(() => {
  if (props.label) return props.label

  return {
    edit: 'Edit',
    delete: 'Delete',
    qrcode: 'QR Code',
    export: 'Export',
    upload: 'Upload',
    view: 'View',
    exclude: 'Exclude',
    include: 'Include',
    load: 'Load',
    save: 'Save',
    restore: 'Restore',
    remove: 'Remove',
    share: 'Share',
  }[props.type]
})

const computedIcon = computed(() => {
  if (props.icon) return props.icon

  return {
    edit: PencilSquareIcon,
    delete: TrashIcon,
    qrcode: QrCodeIcon,
    export: DocumentArrowDownIcon,
    upload: CloudArrowUpIcon,
    view: EyeIcon,
    exclude: MinusCircleIcon,
    include: PlusCircleIcon,
    load: FolderOpenIcon,
    save: ArrowDownOnSquareIcon,
    restore: ArrowPathIcon,
    remove: TrashIcon,
    share: ShareIcon,
  }[props.type]
})
</script>
